import axios from "axios";

const API_URL =
  process.env.REACT_APP_API_URL ||
  `http://${window.location.hostname}:8000/api`;

export const getSocketBaseUrl = () => {
    try {
        // If API_URL is absolute (e.g. http://localhost:8000/api)
        if (API_URL.startsWith('http')) {
            const url = new URL(API_URL);
            return url.origin; // Returns "http://localhost:8000" or "https://api-report.mammy.vn"
        }
        // If relative (e.g. /api), assume same origin as window
        return window.location.origin;
    } catch (e) {
        console.error("Invalid API_URL for socket, using window origin", e);
        return window.location.origin;
    }
};

const api = axios.create({
  baseURL: API_URL,
  withCredentials: true,
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Refresh Token Queue Mechanism
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

api.interceptors.response.use(
  (res) => res,
  async (error) => {
    const original = error.config;

    if (error.response?.status === 503) {
        // Redirect to maintenance page on 503
        if (window.location.pathname !== "/dashboard/online/maintenance") {
             window.location.href = "/dashboard/online/maintenance";
        }
        return Promise.reject(error);
    }

    if (error.response?.status === 401 && !original._retry) {
      // Prevent infinite loop: if the failed request IS the refresh token request, don't retry.
      if (original.url.includes("/auth/refresh")) {
           localStorage.removeItem("token");
           window.location.href = "/login";
           return Promise.reject(error);
      }

      if (isRefreshing) {
        return new Promise(function (resolve, reject) {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            original.headers["Authorization"] = "Bearer " + token;
            return api(original);
          })
          .catch((err) => {
            return Promise.reject(err);
          });
      }

      original._retry = true;
      isRefreshing = true;

      try {
        const res = await api.post("/auth/refresh");
        const newToken = res.data.token;

        localStorage.setItem("token", newToken);
        api.defaults.headers.common["Authorization"] = "Bearer " + newToken;

        processQueue(null, newToken);
        
        original.headers["Authorization"] = "Bearer " + newToken;
        return api(original);
      } catch (err) {
        processQueue(err, null);
        localStorage.removeItem("token");
        window.location.href = "/login";
        return Promise.reject(err);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

// ======================
//  API CALLS
// ======================

// NMV
export const fetchNMV = ({ startDate, endDate, signal }) =>
  api.get("/secure/nmv", { params: { startDate, endDate }, signal });

// DM Product
export const fetchDMProduct = ({ startDate, endDate, signal }) =>
  api.get("/secure/dm_product", { params: { startDate, endDate }, signal });

// Sales Targets
export const fetchSalesTargets = ({ startDate, endDate, signal }) =>
  api.get("/secure/sales-targets", { params: { startDate, endDate }, signal });

// TikTok sources
export const fetchTiktokSources = ({ startDate, endDate, signal }) =>
  api.get("/secure/tiktok_source", { params: { startDate, endDate }, signal });

// TikTok Creator Products
export const fetchTiktokCreatorProducts = ({ startDate, endDate, creatorUsername, contentType, signal }) =>
  api.get("/secure/tiktok_creator_products", { params: { startDate, endDate, creatorUsername, contentType }, signal });

// TikTok Creator Daily Products
export const fetchTiktokCreatorDailyProducts = ({ startDate, endDate, creatorUsername, contentType, signal }) =>
  api.get("/secure/tiktok_creator_daily_products", { params: { startDate, endDate, creatorUsername, contentType }, signal });

// Shopee summary
export const fetchShopeeSummary = ({ startDate, endDate, signal }) =>
  api.get("/secure/shopee_summary", { params: { startDate, endDate }, signal });

// Tiktok summary
export const fetchTiktokSummary = ({ startDate, endDate, signal }) =>
  api.get("/secure/tiktok_summary", { params: { startDate, endDate }, signal });

// INVENTORY
export const fetchInventory = ({
  startDate,
  endDate,
  depotId,
  channelGroup,
  signal,
}) =>
  api.get("/secure/inventory", {
    params: { startDate, endDate, depotId, channelGroup },
    signal,
  });

export const fetchLocationMap = ({
  startDate,
  endDate,
  channels,
  statuses,
  signal,
}) =>
  api.get("/secure/location-map", {
    params: {
      startDate,
      endDate,
      channels: JSON.stringify(channels),
      statuses: JSON.stringify(statuses),
    },
    signal,
  });

//  CUSTOMER 360 / CRM

export const fetchCustomersCRM = (params) =>
  api.get("/secure/customers", { params });

export const fetchCustomerOverview = () =>
  api.get("/secure/customers/overview");

export const fetchCustomerSegments = () =>
  api.get("/secure/customers/segments");

export const fetchCustomerFraud = () => api.get("/secure/customers/fraud");

export const fetchCustomerHistory = (username, channel) =>
  api.get(`/secure/customer/${username}/history`, {
    params: { channel },
  });

// Product Targets
export const fetchProductTargets = ({ startDate, endDate }) =>
  api.get("/secure/product-targets", { params: { startDate, endDate } });

// AUTH
export const login = (username, password) =>
  api.post("/auth/login", { username, password });

export const register = (username, password, role = "user") =>
  api.post("/auth/register", { username, password, role });

export const getCurrentUser = () => api.get("/auth/me");
export const updateProfile = (data) => api.put("/auth/profile", data);
export const uploadAvatar = (formData) => api.post("/auth/avatar", formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
});
export const changePassword = (data) => api.put("/auth/change-password", data);

// ADMIN
export const fetchUsers = () => api.get("/admin/users");

export const deleteUser = (id) => api.delete(`/admin/users/${id}`);

export const updateUserRole = (id, role) =>
  api.put(`/admin/users/${id}`, { role });

export const updateUserStatus = (id, is_active) =>
  api.put(`/admin/users/${id}/status`, { is_active });

export const createUser = (userData) => api.post("/admin/users", userData);

export const resetUserPassword = (id, newPassword) =>
  api.put(`/admin/users/${id}/password`, { newPassword });

// DEAL CALCULATOR
export const fetchDealSuggestions = () => api.get("/secure/deal/suggestions");
export const fetchDealProductPrice = (name, channel, barcode) => 
  api.get("/secure/deal/price", { params: { name, channel, barcode } });
export const fetchDealGiftPrice = (name, channel, barcode) => 
  api.get("/secure/deal/gift-price", { params: { name, channel, barcode } });

// ===== ADMIN: SESSION MANAGEMENT =====

// Lấy session của user
export const fetchUserSessions = (userId) =>
  api.get(`/admin/sessions/${userId}`);

// Kick 1 session
export const revokeSession = (sessionId) =>
  api.post(`/admin/sessions/revoke/${sessionId}`);

// Kick all sessions của user
export const revokeAllSessions = (userId) =>
  api.post(`/admin/sessions/revoke-all/${userId}`);

// Global Logout (Truncate)
export const logoutAllUsers = () => api.delete("/admin/sessions/truncate");

// LOCATIONS
export const fetchCities = () => api.get("/locations/cities");
export const fetchDistricts = (cityId) => api.get(`/locations/districts/${cityId}`);
export const fetchWards = (districtId) => api.get(`/locations/wards/${districtId}`);

// COMPENSATION ORDER
export const fetchCompensationOrders = (params) => api.get("/nhanh/orders", { params });
export const createCompensationDraft = (data) => api.post("/nhanh/order/draft", data);
export const updateCompensationOrder = (id, data) => api.put(`/nhanh/order/${id}`, data);
export const deleteCompensationOrder = (id) => api.delete(`/nhanh/order/${id}`);
export const sendCompensationOrder = (id) => api.post(`/nhanh/order/send/${id}`);

export const AuthenticatedRequest = api;
export default api;
